import React, { PropsWithChildren, useEffect, useState, useMemo } from "react";
import { useSelector } from 'react-redux';
import ErrorText from "../../components/errorText";
import { 
  Users, 
  Tag, 
  List, 
  Bell, 
  Clock, 
  FileSpreadsheet, 
  Facebook,
  CheckCircle,
  Settings,
  Info
} from 'lucide-react';

interface Props {
  actionDrawerTitle: string;
  selectedAction: string;
  handlePlusMinusValue: (e: any, tab: string) => void;
  SubActionSelectOnchange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  delayValue: number;
  statusData: any[];
  labelData: any[];
  leadListData: any[];
  userListData: any[];
  delayMinMaxMsg: string;
  onSaveDrawerClose: (obj: any) => void;
  updateActionDays: string;
  isActionError: boolean;
}

const ActionCommonDrawer: React.FC<PropsWithChildren<Props>> = ({
  actionDrawerTitle,
  selectedAction,
  handlePlusMinusValue,
  SubActionSelectOnchange,
  delayValue,
  statusData,
  labelData,
  leadListData,
  userListData,
  delayMinMaxMsg,
  onSaveDrawerClose,
  updateActionDays,
  isActionError,
}) => {
  // State management for improved UX
  const [isLoading, setIsLoading] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [isUserDataLoading, setIsUserDataLoading] = useState(false);

  // 🔥 DIRECT REDUX ACCESS - Like AssignLeadDrawer does
  const StoreData = useSelector((state: any) => state?.rootReducers);
  const currentUser = StoreData?.userData?.userDetails;
  
  // Get users directly from Redux store (same as AssignLeadDrawer)
  const directUserData = useMemo(() => {
    if (!StoreData?.userData?.userDetails?.organizationEmployee) {
      console.log("❌ No organizationEmployee data in Redux");
      return [];
    }
    
    const users = StoreData.userData.userDetails.organizationEmployee
      .filter((item: any) => 
        // Include active users (don't exclude current user for notifications)
        item?.isActive === true
      )
      .map((item: any) => ({
        _id: item._id,
        firstName: item.firstName || '',
        lastName: item.lastName || '',
        name: `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'Unnamed User',
        role: item.role,
        isActive: item.isActive
      }));
    
    console.log("✅ Direct Redux user data loaded:", {
      totalUsers: users.length,
      activeUsers: users.filter((u: any) => u.isActive).length,
      sampleUser: users[0]
    });
    
    return users;
  }, [StoreData?.userData?.userDetails?.organizationEmployee]);

  // Console logging for debugging - as per user preferences
  useEffect(() => {
    console.log("🎯 ActionCommonDrawer initialized:", {
      actionDrawerTitle,
      selectedAction,
      delayValue,
      updateActionDays,
      isActionError
    });
    
    // Debug user data loading - Compare prop vs direct Redux access
    if (actionDrawerTitle === "Send notification" || actionDrawerTitle === "Assign lead") {
      console.log("👥 USER DATA COMPARISON:", {
        propsUserListLength: userListData?.length || 0,
        directReduxUsersLength: directUserData?.length || 0,
        propsData: userListData,
        directData: directUserData,
        willUseDirectData: directUserData.length > 0
      });
      
      // Use direct Redux data if available, otherwise check props
      const hasUserData = directUserData.length > 0 || (userListData && userListData.length > 0);
      setIsUserDataLoading(!hasUserData);
    }
  }, [actionDrawerTitle, selectedAction, delayValue, updateActionDays, isActionError, userListData, directUserData]);

  // Enhanced label selection logic with detailed step-by-step logic
  const selectLabel = () => {
    console.log("📝 Determining label for action:", actionDrawerTitle);
    
    // Step 1: Check each action type and return appropriate label
    switch (actionDrawerTitle) {
      case "Change status":
        console.log("✅ Label determined: Select Status");
        return "Select Status";
      case "Change Label":
        console.log("✅ Label determined: Select Label");
        return "Select Label";
      case "Move to list":
      case "Copy to list":
        console.log("✅ Label determined: Select Lead list");
        return "Select Lead list";
      case "Send notification":
      case "Assign lead":
        console.log("✅ Label determined: Select user");
        return "Select user";
      case "Delay":
        console.log("✅ Label determined: Select number of days to wait");
        return "Select number of days to wait";
      case "Add to Google Sheet":
        console.log("✅ Label determined: Google Sheet will be auto-created");
        return "Google Sheet will be auto-created";
      case "Add to Facebook Audience":
        console.log("✅ Label determined: Select Facebook Ad Account");
        return "Select Facebook Ad Account";
      default:
        console.log("⚠️ No label found for action:", actionDrawerTitle);
        return "";
    }
  };

  // Enhanced icon logic for visual hierarchy
  const getActionIcon = () => {
    console.log("🎨 Getting icon for action:", actionDrawerTitle);
    
    switch (actionDrawerTitle) {
      case "Change status":
        return <CheckCircle className="w-4 h-4 text-blue-600" />;
      case "Change Label":
        return <Tag className="w-4 h-4 text-purple-600" />;
      case "Move to list":
      case "Copy to list":
        return <List className="w-4 h-4 text-green-600" />;
      case "Send notification":
        return <Bell className="w-4 h-4 text-orange-600" />;
      case "Assign lead":
        return <Users className="w-4 h-4 text-indigo-600" />;
      case "Delay":
        return <Clock className="w-4 h-4 text-gray-600" />;
      case "Add to Google Sheet":
        return <FileSpreadsheet className="w-4 h-4 text-green-500" />;
      case "Add to Facebook Audience":
        return <Facebook className="w-4 h-4 text-blue-500" />;
      default:
        return <Settings className="w-4 h-4 text-gray-500" />;
    }
  };

  // Enhanced save handler with loading state and validation
  const handleSave = async () => {
    console.log("💾 Starting save process for action:", selectedAction);
    setIsLoading(true);
    setValidationMessage("");

    try {
      // Step 1: Initialize config data object
      let configData: any = {};
      console.log("📦 Building config data for action:", selectedAction);

      // Step 2: Build configuration based on selected action type
      switch (selectedAction) {
        case "status_change":
          console.log("🔄 Processing status change:", updateActionDays);
          configData.status = updateActionDays;
          break;
        case "label_change":
          console.log("🏷️ Processing label change:", updateActionDays);
          configData.label = [updateActionDays];
          break;
        case "move_to_list":
        case "copy_to_list":
          console.log("📋 Processing list operation:", updateActionDays);
          configData.targetListId = updateActionDays;
          break;
        case "push_notification":
          console.log("🔔 Processing push notification:", updateActionDays);
          configData.push_notification = updateActionDays;
          break;
        case "assign_lead":
          console.log("👤 Processing lead assignment:", updateActionDays);
          configData.assignToUser = updateActionDays;
          break;
        case "add_to_google_sheet":
          console.log("📊 Processing Google Sheet integration");
          configData = {
            headerMapper: { 
              name: 'Full Name', 
              email: 'Email', 
              phone: 'Phone' 
            },
            sheetId: "" // Empty means create new sheet
          };
          break;
        default:
          console.log("⚠️ Unknown action type:", selectedAction);
      }

      // Step 3: Execute save callback
      console.log("✅ Config data prepared:", configData);
      onSaveDrawerClose(configData);
      
    } catch (error) {
      console.error("❌ Error during save process:", error);
      setValidationMessage("An error occurred while saving. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced options rendering with preserved logic and large list support
  const renderOptions = () => {
    console.log("🎛️ Rendering options for:", actionDrawerTitle);
    
    switch (actionDrawerTitle) {
      case "Change status":
        console.log("📊 Rendering status options, count:", statusData?.length);
        return statusData?.map((data: any, j: number) => (
          <option value={data.value} key={j}>
            {data.name}
          </option>
        ));
      case "Change Label":
        console.log("🏷️ Rendering label options, count:", labelData?.length);
        return labelData?.map((data: any, j: number) => (
          <option value={data.value} key={j}>
            {data.name}
          </option>
        ));
      case "Move to list":
      case "Copy to list":
        console.log("📋 Rendering lead list options, count:", leadListData?.length);
        return leadListData?.map((data: any, j: number) => (
          <option value={data._id} key={j}>
            {data.name}
          </option>
        ));
      case "Send notification":
      case "Assign lead":
        // 🔥 USE DIRECT REDUX DATA INSTEAD OF PROPS
        const usersToShow = directUserData.length > 0 ? directUserData : userListData || [];
        
        console.log("👥 Rendering user options:", {
          source: directUserData.length > 0 ? 'Direct Redux' : 'Props',
          count: usersToShow.length,
          sampleUsers: usersToShow.slice(0, 3).map((u: any) => ({ id: u._id, name: u.name || `${u.firstName} ${u.lastName}` }))
        });
        
        // Enhanced user rendering with full name support
        return usersToShow?.map((data: any, j: number) => {
          const displayName = data.name || 
            (data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : 
             data.firstName || 'Unnamed User');
          
          return (
            <option value={data._id} key={j} title={displayName}>
              {displayName}
            </option>
          );
        });
      default:
        console.log("❌ No options available for:", actionDrawerTitle);
        return null;
    }
  };

  // Enhanced render with improved styling and UX
  return (
    <div className="enhanced-action-drawer">
      {/* Header Section with Icon and Title */}
      <div className="action-header" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px 0 12px 0',
        borderBottom: '1px solid #e5e7eb',
        marginBottom: '20px'
      }}>
        {getActionIcon()}
        <h3 style={{
          margin: 0,
          fontSize: '16px',
          fontWeight: '600',
          color: '#374151'
        }}>
          {actionDrawerTitle}
        </h3>
      </div>

      {/* Main Content Area */}
      <div className="row">
        <div className="form-group action_select" style={{ marginBottom: '24px' }}>
          {/* Delay Action - Special UI */}
          {actionDrawerTitle === "Delay" ? (
            <div className="delay-section">
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  {selectLabel()}
                </label>
                
                {/* Enhanced delay input with better styling */}
                <div className="delay_plus_minus" style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: 'white'
                }}>
                  <div 
                    className="plush_minus_box_left"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '40px',
                      height: '44px',
                      backgroundColor: '#f9fafb',
                      borderRight: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  >
                    <i
                      className="bi bi-dash"
                      onClick={(e: any) => handlePlusMinusValue(e, "minus")}
                      style={{ fontSize: '16px', color: '#6b7280' }}
                    />
                  </div>
                  
                  <input
                    type="number"
                    min="1"
                    max="366"
                    name="delay"
                    className="form-control plus_minus"
                    value={delayValue}
                    onChange={(e: any) => handlePlusMinusValue(e, "")}
                    style={{
                      border: 'none',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: '500',
                      padding: '12px 16px',
                      outline: 'none',
                      flex: 1
                    }}
                  />
                  
                  <div 
                    className="plush_minus_box_right"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '40px',
                      height: '44px',
                      backgroundColor: '#f9fafb',
                      borderLeft: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  >
                    <i
                      className="bi bi-plus"
                      onClick={(e: any) => handlePlusMinusValue(e, "plus")}
                      style={{ fontSize: '16px', color: '#6b7280' }}
                    />
                  </div>
                </div>
              </div>
              
              {/* Enhanced error message display */}
              {delayMinMaxMsg && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  color: '#dc2626',
                  fontSize: '13px'
                }}>
                  <Info className="w-4 h-4" />
                  {delayMinMaxMsg}
                </div>
              )}
            </div>
          ) : actionDrawerTitle === "Add to Google Sheet" ? (
            /* Google Sheet Action - Special UI */
            <div className="google-sheet-section">
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '12px'
              }}>
                Google Sheet Configuration
              </label>
              
              <div style={{
                padding: '16px',
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <Info className="w-5 h-5 text-blue-600 mt-0.5" style={{ flexShrink: 0 }} />
                <div>
                  <p style={{
                    margin: 0,
                    fontSize: '13px',
                    color: '#1e40af',
                    lineHeight: '1.5'
                  }}>
                    A new Google Sheet will be created automatically with columns: 
                    <strong> Full Name, Email, Phone</strong>
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Standard Select Action */
            <div className="select-section">
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                {selectLabel()}
              </label>
              
              {/* Enhanced select with modern styling and large list support */}
              <select
                name={selectedAction}
                className="form-select enhanced-user-select"
                value={updateActionDays}
               
                disabled={isUserDataLoading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: isUserDataLoading ? '#f9fafb' : 'white',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                  // Enhanced styling for large lists
                  maxHeight: 'none', // Remove height restrictions
                  overflowY: 'auto' // Enable scrolling
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  console.log("🎯 Select focused - available options:", e.target.options.length);
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                onChange={(e) => {
                  console.log("📝 Select changed to:", e.target.value);
                  console.log("📝 Selected option text:", e.target.options[e.target.selectedIndex]?.text);
                  SubActionSelectOnchange(e);
                }}
              >
                <option value="" style={{ color: '#9ca3af' }}>
                  {isUserDataLoading ? "Loading users..." : `Select ${selectLabel()}`}
                </option>
                {renderOptions()}
              </select>
              
              {/* Helper text for large user lists */}
              {(actionDrawerTitle === "Send notification" || actionDrawerTitle === "Assign lead") && (
                (() => {
                  const usersToShow = directUserData.length > 0 ? directUserData : userListData || [];
                  return usersToShow && usersToShow.length > 10 && (
                    <div style={{
                      marginTop: '6px',
                      fontSize: '12px',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <Info className="w-3 h-3" />
                      {usersToShow.length} users available - scroll to see all options
                    </div>
                  );
                })()
              )}
              
              {/* Loading indicator for user data */}
              {isUserDataLoading && (actionDrawerTitle === "Send notification" || actionDrawerTitle === "Assign lead") && (
                <div style={{
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '6px',
                  color: '#0369a1',
                  fontSize: '13px'
                }}>
                  <div className="spinner" style={{
                    width: '12px',
                    height: '12px',
                    border: '2px solid #0369a140',
                    borderTop: '2px solid #0369a1',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Loading user data from Redux store...
                </div>
              )}
              
              {/* Enhanced error display */}
              {isActionError && (
                <div style={{ marginTop: '8px' }}>
                  <ErrorText message={`${selectLabel()} is required.`} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Enhanced Action Buttons */}
        <div className="auto_action_form_btn" style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          paddingTop: '20px',
          borderTop: '1px solid #e5e7eb',
          marginTop: '20px'
        }}>
          {/* Save Button with Loading State */}
          <button
            type="button"
            className="btn btn-primary-save"
            onClick={handleSave}
            disabled={isLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              backgroundColor: isLoading ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              minWidth: '100px'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = '#2563eb';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = '#3b82f6';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            {isLoading ? (
              <>
                <div className="spinner" style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid #ffffff40',
                  borderTop: '2px solid #ffffff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Save
              </>
            )}
          </button>
        </div>

        {/* Validation Message Display */}
        {validationMessage && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#dc2626',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Info className="w-4 h-4" />
            {validationMessage}
          </div>
        )}
      </div>

      {/* Add keyframe animation for spinner and enhanced dropdown styles */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          /* Enhanced dropdown styling for large user lists */
          .enhanced-user-select {
            appearance: none;
            background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
            background-repeat: no-repeat;
            background-position: right 12px center;
            background-size: 16px;
            padding-right: 40px;
          }
          
          /* Enhanced dropdown options styling */
          .enhanced-user-select option {
            padding: 8px 12px !important;
            font-size: 14px !important;
            line-height: 1.4 !important;
            background-color: white !important;
            color: #374151 !important;
            border: none !important;
          }
          
          .enhanced-user-select option:hover {
            background-color: #f3f4f6 !important;
          }
          
          .enhanced-user-select option:checked,
          .enhanced-user-select option:focus {
            background-color: #3b82f6 !important;
            color: white !important;
          }
          
          /* Ensure dropdown opens properly for large lists */
          .enhanced-user-select:focus {
            z-index: 1000;
          }
          
          /* Firefox specific fixes for dropdown */
          @-moz-document url-prefix() {
            .enhanced-user-select {
              text-indent: 0.01px;
              text-overflow: '';
            }
            
            .enhanced-user-select option {
              background-color: white;
              color: #374151;
            }
          }
          
          /* Safari specific fixes */
          @supports (-webkit-appearance: none) {
            .enhanced-user-select option {
              background-color: white;
              color: #374151;
            }
          }
          
          /* Enhanced loading state for selects */
          .enhanced-user-select:disabled {
            background-color: #f9fafb;
            color: #9ca3af;
            cursor: not-allowed;
          }
          
          /* Better focus states */
          .enhanced-user-select:focus-visible {
            outline: 2px solid #3b82f6;
            outline-offset: 2px;
          }
        `}
      </style>
    </div>
  );
};

export default ActionCommonDrawer;